import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getRequest, setResponseHeaders } from "@tanstack/react-start/server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { regeneratePostAiSummary } from "@/lib/ai-summaries.server";
import { resolveTags } from "@/lib/tags";
import type { WpArticle, WpArticleFull, WpCategory } from "@/lib/posts.types";
import { canonicalSlug, displaySlug } from "@/lib/slug-aliases";

export type Lang = "en" | "tr";

// ===== TR -> EN translation for admin editor =====

async function aiTranslateTrToEn(payload: {
  title: string;
  excerpt: string;
  content: string;
}): Promise<{ title: string; excerpt: string; content: string }> {
  const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
  if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY is not configured");
  const system =
    "You translate an article from Turkish to natural, fluent English. " +
    "The 'content' field is HTML — preserve ALL HTML tags, attributes, and structure exactly; " +
    "translate ONLY the human-readable text between tags. Keep proper nouns and brand names. " +
    'Return ONLY a JSON object: {"title":"...","excerpt":"...","content":"..."}.';
  const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${LOVABLE_API_KEY}`,
    },
    body: JSON.stringify({
      model: "google/gemini-3-flash-preview",
      messages: [
        { role: "system", content: system },
        { role: "user", content: JSON.stringify(payload) },
      ],
      response_format: { type: "json_object" },
    }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`AI translate failed [${res.status}]: ${body}`);
  }
  const data = (await res.json()) as { choices: Array<{ message: { content: string } }> };
  const raw = data.choices?.[0]?.message?.content ?? "{}";
  const out = JSON.parse(raw) as { title?: string; excerpt?: string; content?: string };
  return {
    title: out.title ?? payload.title,
    excerpt: out.excerpt ?? payload.excerpt,
    content: out.content ?? payload.content,
  };
}

async function googleTrToEn(text: string): Promise<string> {
  if (!text.trim()) return text;
  const res = await fetch(
    "https://translate.googleapis.com/translate_a/single?client=gtx&sl=tr&tl=en&dt=t",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "User-Agent": "Mozilla/5.0",
      },
      body: new URLSearchParams({ q: text }),
    },
  );
  if (!res.ok) throw new Error(`Google translate failed [${res.status}]`);
  const json = (await res.json()) as Array<Array<Array<string>>>;
  return (json[0] ?? []).map((p) => p[0]).join("") || text;
}

export const translateTrToEn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        title: z.string().max(300).default(""),
        excerpt: z.string().max(2000).default(""),
        content: z.string().max(200_000).default(""),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (!(roles ?? []).some((r) => r.role === "admin")) throw new Error("Unauthorized");

    try {
      return await aiTranslateTrToEn(data);
    } catch (e) {
      console.error("AI TR->EN failed, falling back:", e);
      const [title, excerpt, content] = await Promise.all([
        googleTrToEn(data.title),
        googleTrToEn(data.excerpt),
        googleTrToEn(data.content),
      ]);
      return { title, excerpt, content };
    }
  });

const CATEGORY_TR: Record<string, string> = {
  "AI & Society": "Yapay Zekâ & Toplum",
  "Climate & Systems": "İklim & Sistemler",
  "Human Futures": "İnsan ve Gelecek",
  "Sustainability Critique": "Sürdürülebilirlik Eleştirisi",
  Other: "Diğer",
};

function formatDate(iso: string, lang: Lang) {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "tr" ? "tr-TR" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function readTime(text: string, lang: Lang) {
  const words = text.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} ${lang === "tr" ? "dk" : "min"}`;
}

/** Turkish-aware slugifier: ş→s, ı→i, etc., then strip non-alphanumerics. */
function slugifyTr(input: string): string {
  const map: Record<string, string> = {
    ç: "c", Ç: "c", ğ: "g", Ğ: "g", ı: "i", İ: "i",
    ö: "o", Ö: "o", ş: "s", Ş: "s", ü: "u", Ü: "u",
  };
  return input
    .replace(/[çÇğĞıİöÖşŞüÜ]/g, (c) => map[c] ?? c)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function identityKey(input?: string | null): string {
  const map: Record<string, string> = {
    ç: "c", Ç: "c", ğ: "g", Ğ: "g", ı: "i", İ: "i",
    ö: "o", Ö: "o", ş: "s", Ş: "s", ü: "u", Ü: "u",
  };
  return (input ?? "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#8217;|&#8216;/g, "'")
    .replace(/[çÇğĞıİöÖşŞüÜ]/g, (c) => map[c] ?? c)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}


function resolveLang(input?: Lang): Lang {
  if (input === "tr") return "tr";
  if (input === "en") return "en";
  const cookie = getRequest()?.headers.get("cookie") ?? "";
  // Site is Turkish-first: default to TR when no explicit cookie (this also
  // ensures social crawlers like X/LinkedIn — which don't send cookies — get
  // Turkish og:title/description on shared post previews).
  return /(?:^|;\s*)lang=en(?:;|$)/.test(cookie) ? "en" : "tr";
}

interface DbPost {
  id: string;
  slug: string;
  title_en: string;
  title_tr: string;
  excerpt_en: string;
  excerpt_tr: string;
  content_en: string;
  content_tr: string;
  category: string;
  cover_image_url: string | null;
  published: boolean;
  published_at: string | null;
  show_on_home?: boolean;
  created_at: string;
  updated_at: string;
  audio_url?: string | null;
  audio_lang?: string | null;
  audio_duration_seconds?: number | null;
  external_sources?: { url: string; title: string; host: string }[] | null;
  tags?: string[] | null;
  seo_title?: string | null;
  post_type?: string | null;
  collection?: string | null;
  related_slugs?: string[] | null;
  book_id?: string | null;
  scheduled_at?: string | null;
}

function dbToArticle(p: DbPost, lang: Lang): WpArticle {
  const isTr = lang === "tr";
  const title = (isTr ? p.title_tr : p.title_en) || p.title_en || p.title_tr;
  const excerpt = (isTr ? p.excerpt_tr : p.excerpt_en) || p.excerpt_en || p.excerpt_tr;
  const iso = p.published_at ?? p.created_at;
  const cat = (isTr ? CATEGORY_TR[p.category] ?? p.category : p.category) as WpCategory;
  const audioMatches = !!p.audio_url && (!p.audio_lang || p.audio_lang === lang);
  return {
    slug: p.slug,
    title,
    excerpt: excerpt.slice(0, 240),
    date: formatDate(iso, lang),
    isoDate: iso,
    updatedIso: p.updated_at && p.updated_at > iso ? p.updated_at : iso,
    readTime: readTime((isTr ? p.content_tr : p.content_en) || excerpt, lang),
    category: cat,
    url: `/yazilar/${p.slug}`,
    img: p.cover_image_url,
    hasAudio: audioMatches,
    audioDurationSeconds: audioMatches ? p.audio_duration_seconds ?? null : null,
    // Resolve curated and editor-authored tags through the shared helper so the
    // page, tag archives and API/MCP layer always report the same tags.
    tags: resolveTags(p),
  };
}

function dbToFull(p: DbPost, lang: Lang): WpArticleFull {
  const base = dbToArticle(p, lang);
  const isTr = lang === "tr";
  const content = (isTr ? p.content_tr : p.content_en) || p.content_en || p.content_tr;
  const pickList = (raw: unknown): string[] => {
    if (!raw || typeof raw !== "object") return [];
    const key = isTr ? "tr" : "en";
    const v = (raw as Record<string, unknown>)[key];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  };
  return {
    ...base,
    content,
    audioUrl: p.audio_url ?? null,
    audioLang: p.audio_lang ?? null,
    audioDurationSeconds: p.audio_duration_seconds ?? null,
    summaryBullets: pickList((p as unknown as { summary_bullets?: unknown }).summary_bullets),
    keyTakeaways: pickList((p as unknown as { key_takeaways?: unknown }).key_takeaways),
    learningOutcomes: pickList((p as unknown as { learning_outcomes?: unknown }).learning_outcomes),
    externalSources: p.external_sources ?? undefined,
    seoTitle: isTr ? ((p as unknown as { seo_title?: string | null }).seo_title ?? null) : null,
    relatedSlugs: (p as unknown as { related_slugs?: string[] | null }).related_slugs ?? [],
    bookId: (p as unknown as { book_id?: string | null }).book_id ?? null,
  };
}

/** All published posts from the local `posts` table, sorted by date desc. */
export const getAllPosts = createServerFn({ method: "GET" })
  .inputValidator((d?: { lang?: Lang }) => d ?? {})
  .handler(async ({ data }): Promise<WpArticle[]> => {
    const lang = resolveLang(data.lang);

    // Short TTL on purpose: a 1-hour edge cache with a 24-hour
    // stale-while-revalidate window kept a freshly published essay off the
    // homepage for up to a day. 60s keeps the feed cheap but near-live.
    setResponseHeaders(
      new Headers({
        "Cache-Control": "public, max-age=30, s-maxage=60, stale-while-revalidate=120",
      }),
    );


    const { data: rows } = await supabaseAdmin
      .from("posts")
      .select("*")
      .eq("published", true)
      .order("published_at", { ascending: false })
      .limit(500);

    const all = (rows ?? []).map((p) => dbToArticle(p as DbPost, lang));
    all.sort((a, b) => (a.isoDate < b.isoDate ? 1 : -1));
    return all;
  });

/** Same as getAllPosts but excludes posts flagged show_on_home = false. Used by the homepage.
 *  If admin has set a manual home_layout, returns posts in that exact order instead. */
export const getHomePosts = createServerFn({ method: "GET" })
  .inputValidator((d?: { lang?: Lang }) => d ?? {})
  .handler(async ({ data }): Promise<WpArticle[]> => {
    const all = await getAllPosts({ data: { lang: data.lang } });
    const bySlug = new Map(all.map((p) => [p.slug, p]));

    const { data: layout } = await supabaseAdmin
      .from("home_layout")
      .select("position, slug")
      .order("position", { ascending: true });

    const { data: hidden } = await supabaseAdmin
      .from("posts")
      .select("slug")
      .eq("published", true)
      .eq("show_on_home", false);
    const hiddenSet = new Set((hidden ?? []).map((r) => r.slug));

    if (layout && layout.length > 0) {
      const picked = layout
        .map((r) => bySlug.get(r.slug))
        .filter((p): p is WpArticle => Boolean(p));
      if (picked.length > 0) {
        // The curated layout is often not updated the day a post ships, which
        // silently keeps the newest essay off the homepage. Anything newer than
        // the newest curated entry is prepended automatically.
        const pickedSlugs = new Set(picked.map((p) => p.slug));
        const newestCurated = picked.reduce(
          (acc, p) => (p.isoDate > acc ? p.isoDate : acc),
          "",
        );
        const fresher = all.filter(
          (p) =>
            !pickedSlugs.has(p.slug) &&
            !hiddenSet.has(p.slug) &&
            p.isoDate > newestCurated,
        );
        // `all` is already sorted newest-first.
        return [...fresher, ...picked];
      }
    }

    return all.filter((p) => !hiddenSet.has(p.slug));
  });


export const getHomeLayout = createServerFn({ method: "GET" })
  .handler(async (): Promise<{ position: number; slug: string }[]> => {
    const { data } = await supabaseAdmin
      .from("home_layout")
      .select("position, slug")
      .order("position", { ascending: true });
    return data ?? [];
  });

export const saveHomeLayout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        slugs: z.array(z.string().min(1).max(200)).max(50),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertAdminCtx(supabase, userId);
    await supabaseAdmin.from("home_layout").delete().gte("position", 0);
    const rows = data.slugs
      .map((slug, position) => ({ position, slug: slug.trim() }))
      .filter((r) => r.slug.length > 0);
    if (rows.length > 0) {
      const { error } = await supabaseAdmin.from("home_layout").insert(rows);
      if (error) throw error;
    }
    return { ok: true, count: rows.length };
  });


/** Merged single post — local first, then WP. */
export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string; lang?: Lang }) => d)
  .handler(async ({ data }): Promise<WpArticleFull | null> => {
    const lang = resolveLang(data.lang);
    // If a TR alias slug was requested, map it back to the canonical slug
    // before hitting the DB; also try the TR display form.
    const lookupSlug = canonicalSlug(data.slug);
    const slugs = Array.from(new Set([data.slug, lookupSlug, displaySlug(lookupSlug, "tr")]));

    const { data: localRows, error } = await supabaseAdmin
      .from("posts")
      .select("*")
      .in("slug", slugs)
      .eq("published", true)
      .limit(1);

    if (error) console.error("local post fetch error:", error);
    const local = localRows?.[0];
    if (local) {
      setResponseHeaders(
        new Headers({ "Cache-Control": "public, max-age=60, s-maxage=300" }),
      );
      const full = dbToFull(local as DbPost, lang);
      // Only sources the editor explicitly approved are ever published.
      const { data: src } = await supabaseAdmin
        .from("post_sources")
        .select("title, publisher, url, source_date, sort_order, relation, decided_at")
        .eq("post_id", (local as DbPost).id)
        .eq("status", "approved")
        .order("sort_order", { ascending: true });
      return {
        ...full,
        approvedSources: (src ?? []).map((r) => ({ title: r.title, publisher: r.publisher, url: r.url, date: r.source_date, relation: (r.relation === "later_addition" ? "later_addition" : "original") as "original" | "later_addition", addedAt: r.relation === "later_addition" ? r.decided_at : null })),
      };
    }

    return null;
  });

// ============= Admin server functions =============

const postInput = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9-]+$/, "Slug: küçük harf, rakam, tire"),
  title_en: z.string().max(300).default(""),
  title_tr: z.string().max(300).default(""),
  excerpt_en: z.string().max(1000).default(""),
  excerpt_tr: z.string().max(1000).default(""),
  content_en: z.string().max(200_000).default(""),
  content_tr: z.string().max(200_000).default(""),
  category: z.string().max(100).default("Other"),
  cover_image_url: z.string().url().nullable().optional(),
  cover_meta: z
    .object({
      source: z.enum(["pexels", "upload"]),
      alt: z.string().max(300),
      focal: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }),
      width: z.number().int().positive(),
      height: z.number().int().positive(),
      photographer: z.string().max(200).optional(),
      photographer_url: z.string().url().max(500).optional(),
      source_url: z.string().url().max(500).optional(),
      original_url: z.string().url().max(500).optional(),
      license: z.string().max(300).optional(),
      license_url: z.string().url().max(300).optional(),
    })
    .nullable()
    .optional(),
  published: z.boolean().default(false),
  show_on_home: z.boolean().default(true),
  // Curated or editor-authored tags; normalization is
  // applied by resolveTags before persistence.
  tags: z.array(z.string().max(60)).max(50).default([]),
  seo_title: z.string().max(120).nullable().optional(),
  post_type: z.enum(["yazi", "not"]).optional(),
  collection: z.enum(["cop31"]).nullable().optional(),
  related_slugs: z.array(z.string().regex(/^[a-z0-9-]+$/).max(200)).max(6).optional(),
  book_id: z.enum(["space", "loneliness", "ai", "climate"]).nullable().optional(),
  scheduled_at: z.string().datetime({ offset: true }).nullable().optional(),
});

export const listAdminPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    // Verify admin via has_role check using a count on user_roles
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    const isAdmin = (roles ?? []).some((r) => r.role === "admin");
    if (!isAdmin) throw new Error("Unauthorized");

    const { data, error } = await supabaseAdmin
      .from("posts")
      .select("id, slug, title_en, title_tr, excerpt_tr, cover_image_url, tags, category, published, published_at, created_at, updated_at")
      .order("published_at", { ascending: false, nullsFirst: true })
      .order("created_at", { ascending: false });
    if (error) throw error;
    return { posts: data ?? [], isAdmin: true };
  });

export const getAdminPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (!(roles ?? []).some((r) => r.role === "admin")) throw new Error("Unauthorized");
    const { data: post, error } = await supabaseAdmin
      .from("posts")
      .select("*")
      .eq("id", data.id)
      .single();
    if (error) throw error;
    return post as DbPost;
  });

export const savePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => postInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (!(roles ?? []).some((r) => r.role === "admin")) throw new Error("Unauthorized");
    // Technical publish blockers are enforced server-side too (same rules as the readiness panel).
    if (data.scheduled_at) {
      if (data.published) throw new Error("Yayındaki yazı zamanlanamaz.");
      const t = Date.parse(data.scheduled_at);
      if (t < Date.now() + 10 * 60_000) throw new Error("Zamanlanmış yayın en az 10 dakika sonrası olmalı.");
      if (new Date(t).getUTCMinutes() !== 0) throw new Error("Zamanlanmış yayın tam saatte olmalı.");
    }
    if (data.published || data.scheduled_at) {
      if (!data.title_tr.trim()) throw new Error("Yayımlanamaz: başlık yok.");
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(data.slug)) throw new Error("Yayımlanamaz: kalıcı bağlantı geçersiz.");
      if (!data.content_tr.replace(/<[^>]+>/g, "").trim()) throw new Error("Yayımlanamaz: metin boş.");
      const { data: clash } = await supabase.from("posts").select("id").eq("slug", data.slug).neq("id", data.id ?? "00000000-0000-0000-0000-000000000000").limit(1);
      if (clash?.length) throw new Error("Yayımlanamaz: bu kalıcı bağlantı başka bir yazıda kullanılıyor.");
    }

    const payload = {
      slug: data.slug,
      title_en: data.title_en,
      title_tr: data.title_tr,
      excerpt_en: data.excerpt_en,
      excerpt_tr: data.excerpt_tr,
      content_en: data.content_en,
      content_tr: data.content_tr,
      category: data.category,
      cover_image_url: data.cover_image_url ?? null,
      // Only written when the editor sends it, so legacy covers keep a null meta.
      ...(data.cover_meta !== undefined ? { cover_meta: data.cover_meta } : {}),
      published: data.published,
      show_on_home: data.show_on_home,
      ...(data.seo_title !== undefined ? { seo_title: data.seo_title?.trim() || null } : {}),
      ...(data.post_type !== undefined ? { post_type: data.post_type } : {}),
      ...(data.collection !== undefined ? { collection: data.collection } : {}),
      ...(data.related_slugs !== undefined ? { related_slugs: data.related_slugs.filter((x) => x !== data.slug) } : {}),
      ...(data.book_id !== undefined ? { book_id: data.book_id } : {}),
      ...(data.scheduled_at !== undefined ? { scheduled_at: data.published ? null : data.scheduled_at } : {}),
      // Persist the resolved tags so the stored row matches what the page
      // renders: an admin who saves without picking tags still gets the
      // derived ones written to the database (and therefore to the API).
      tags: resolveTags({
        tags: data.tags,
        title_tr: data.title_tr,
        title_en: data.title_en,
        excerpt_tr: data.excerpt_tr,
        excerpt_en: data.excerpt_en,
        content_tr: data.content_tr,
        content_en: data.content_en,
      }),
      published_at:
        data.published && !data.id
          ? new Date().toISOString()
          : undefined,
      // Attribution: the byline (author_ref) and creator fields are set once on
      // insert and protected by a DB trigger; saves only record the last editor.
      updated_by: userId,
      // Editor approval token: the DB guard accepts body changes only with a fresh token from an admin save.
      body_edit_token: crypto.randomUUID(),
    };

    if (data.id) {
      const { data: existing } = await supabaseAdmin
        .from("posts")
        .select("published_at")
        .eq("id", data.id)
        .single();
      const publishedAt = existing?.published_at
        ? existing.published_at
        : data.published
          ? new Date().toISOString()
          : null;
      const { data: updated, error } = await supabaseAdmin
        .from("posts")
        .update({ ...payload, published_at: publishedAt, ai_summary_generated_at: null })
        .eq("id", data.id)
        .select()
        .single();
      if (error) throw error;
      if (updated.published) {
        regeneratePostAiSummary(updated.id).catch((e) =>
          console.error("[savePost] AI summary regen failed", updated.slug, e),
        );
      }
      return { id: updated.id, slug: updated.slug };
    } else {
      const { data: created, error } = await supabaseAdmin
        .from("posts")
        .insert({ ...payload, author_id: userId, created_by: userId })
        .select()
        .single();
      if (error) throw error;
      if (created.published) {
        regeneratePostAiSummary(created.id).catch((e) =>
          console.error("[savePost] AI summary regen failed", created.slug, e),
        );
      }
      return { id: created.id, slug: created.slug };
    }
  });

export const deletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (!(roles ?? []).some((r) => r.role === "admin")) throw new Error("Unauthorized");
    const { error } = await supabaseAdmin.from("posts").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    return { isAdmin: (roles ?? []).some((r) => r.role === "admin"), userId };
  });

async function assertAdminCtx(supabase: any, userId: string) {
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (!(roles ?? []).some((r: { role: string }) => r.role === "admin")) {
    throw new Error("Unauthorized");
  }
}

// (WordPress importer removed — all content lives in the local `posts` table.)


/**
 * Upload a base64-encoded PNG (e.g. canvas-rendered OG image) to the
 * post-images bucket using the service role. Bypasses storage RLS so the
 * upload doesn't intermittently fail when the user's session is stale.
 * Admin-gated server-side.
 */
export const uploadOgImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        // data URL like "data:image/png;base64,iVBOR..."
        dataUrl: z.string().min(32).max(8_000_000),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdminCtx(context.supabase, context.userId);
    const match = /^data:image\/png;base64,(.+)$/.exec(data.dataUrl);
    if (!match) throw new Error("Invalid PNG data URL");
    const bytes = Buffer.from(match[1], "base64");
    const path = `og/${crypto.randomUUID()}.png`;
    const { error } = await supabaseAdmin.storage
      .from("post-images")
      .upload(path, bytes, { contentType: "image/png", cacheControl: "31536000" });
    if (error) throw new Error(error.message);
    const { data: pub } = supabaseAdmin.storage.from("post-images").getPublicUrl(path);
    return { url: pub.publicUrl };
  });
